import React from 'react';
import styled from 'styled-components';

import MediaLabel from '../../../components/media-label/MediaLabel';
import {
  MediaItem,
  MediaItemProducerTag,
  PRODUCER_TAG_LABELS,
  PRODUCER_TAG_TONES,
} from '../../../app/services/mediaItems';

/**
 * プロデューサー側タグ（オススメ / 調整前提 / 別案）の右上オーバーレイ。
 *
 * ラベル本体はグローバル `MediaLabel`（StatusBadge「旧環境」chip と同じ
 * 小型デザイン）。本コンポーネントはカード / メディア右上への配置と、
 * クリック切替のロジックだけを持つ。
 *
 * 表示箇所は比較グループ（`ComparisonSlide` の候補表示）のみ。単体
 * バリエーションには表示しない（候補が複数あるときだけ意味を持つため）。
 *
 * 色はタグごとに固定（`PRODUCER_TAG_TONES`）:
 * - オススメ: soremo-blue
 * - 別案: soremo-gray
 * - 調整前提: soremo-background 地 + soremo-border 枠
 *
 * - 編集可能（プロデューサー側）: 候補ラベルを並べ、ワンクリックで切替・再クリックで解除
 * - 読み取り専用（クライアント側等）: 設定済みタグのみ表示
 */

const PRODUCER_TAG_KEYS = Object.keys(PRODUCER_TAG_LABELS) as MediaItemProducerTag[];

// ===== Styled Components =====

const OverlayRow = styled.div<{ $offsetRight: number }>`
  position: absolute;
  top: 8px;
  right: ${p => p.$offsetRight}px;
  z-index: 6;
  display: flex;
  gap: 4px;
`;

// ===== Props =====

export interface ProducerTagOverlayProps {
  item: MediaItem;
  /** プロデューサー側ユーザーなら true（候補ラベル表示 + クリック切替） */
  editable: boolean;
  // eslint-disable-next-line no-unused-vars
  onToggle?: (item: MediaItem, tag: MediaItemProducerTag) => void;
  /** TakeLabel と重なる場合に右オフセットを広げる（px） */
  offsetRight?: number;
}

// ===== Component =====

const ProducerTagOverlay: React.FC<ProducerTagOverlayProps> = ({
  item,
  editable,
  onToggle,
  offsetRight = 12,
}) => {
  const active = item.producer_tag ?? null;

  if (!editable) {
    if (!active) return null;
    return (
      <OverlayRow $offsetRight={offsetRight}>
        <MediaLabel tone={PRODUCER_TAG_TONES[active]}>
          {PRODUCER_TAG_LABELS[active]}
        </MediaLabel>
      </OverlayRow>
    );
  }

  return (
    <OverlayRow $offsetRight={offsetRight}>
      {PRODUCER_TAG_KEYS.map(key => (
        <MediaLabel
          key={key}
          tone={active === key ? PRODUCER_TAG_TONES[key] : 'muted'}
          onClick={e => {
            e.stopPropagation();
            onToggle?.(item, key);
          }}
        >
          {PRODUCER_TAG_LABELS[key]}
        </MediaLabel>
      ))}
    </OverlayRow>
  );
};

export default ProducerTagOverlay;
